import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Star, ShieldCheck, X } from 'lucide-react';
import { Button } from './Button';
import { initSocket } from '../services/socketService';
import { getOpenReviewUitnodigingen, stelReviewUit } from '../services/reviewsApi';
import type { OpenReviewUitnodiging } from '../services/reviewsApi';

export const QK_OPEN_REVIEWS = ['medewerker-reviews-openstaand'] as const;

/** Open review-uitnodigingen van de ingelogde medewerker; ververst realtime op het socket-event. */
export function useOpenReviewUitnodigingen() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: QK_OPEN_REVIEWS, queryFn: getOpenReviewUitnodigingen, refetchOnWindowFocus: true });

  useEffect(() => {
    const socket = initSocket();
    if (!socket) return;
    const ververs = () => queryClient.invalidateQueries({ queryKey: QK_OPEN_REVIEWS });
    socket.on('review_uitnodiging', ververs);
    return () => {
      socket.off('review_uitnodiging', ververs);
    };
  }, [queryClient]);

  return query;
}

/**
 * In-app Review-Modal (Instant Experience Feedback Loop). Verschijnt zodra er
 * een open uitnodiging is: na een voltooid gesprek of afgesloten traject.
 * [Nu beoordelen] opent de anonieme wizard; [Later] verbergt hem 24 uur in-app
 * (de maillink blijft geldig).
 */
export const ReviewUitnodigingModal: React.FC<{ uitnodiging: OpenReviewUitnodiging; onSluit: () => void }> = ({ uitnodiging, onSluit }) => {
  const { t } = useTranslation('medewerker');
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const laterM = useMutation({
    mutationFn: () => stelReviewUit(uitnodiging.id, 24),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QK_OPEN_REVIEWS });
      onSluit();
    },
  });

  return (
    <div className="fixed inset-0 z-40 bg-black/40 flex items-end sm:items-center justify-center" onClick={() => laterM.mutate()}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-modal-titel"
        className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-amber-50 p-2">
              <Star className="w-5 h-5 text-amber-500 fill-amber-400" aria-hidden="true" />
            </span>
            <h2 id="review-modal-titel" className="font-semibold text-gray-900">
              {t('review.modalTitel', { defaultValue: 'Uw anonieme ervaring is goud waard' })}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => laterM.mutate()}
            aria-label={t('algemeen.sluiten', { defaultValue: 'Sluiten' })}
            className="p-1 text-gray-400 hover:text-gray-700"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        <p className="text-sm text-gray-700 mt-3">
          {t('review.modalTekst', {
            defaultValue: '{{bron}}: hoe was uw ervaring met {{naam}}?',
            bron: uitnodiging.bron_label,
            naam: uitnodiging.professional_naam,
          })}
        </p>
        {uitnodiging.discipline_label && <p className="text-xs text-gray-500 mt-0.5">{uitnodiging.discipline_label}</p>}
        <p className="text-xs text-gray-600 mt-3 flex items-start gap-1.5">
          <ShieldCheck className="w-4 h-4 text-green-600 shrink-0" aria-hidden="true" />
          {t('review.modalPrivacy', {
            defaultValue: '{{n}} sterrenvragen en één open vraag, minder dan 45 seconden. Uw werkgever ziet nooit wie dit invult.',
            n: uitnodiging.aantal_sterrenvragen,
          })}
        </p>

        <div className="mt-5 flex flex-col gap-2">
          <Button size="lg" className="w-full" onClick={() => navigate(uitnodiging.review_url)}>
            {t('review.nuBeoordelen', { defaultValue: 'Nu beoordelen' })}
          </Button>
          <Button variant="outline" size="lg" className="w-full" onClick={() => laterM.mutate()} disabled={laterM.isPending}>
            {t('review.later', { defaultValue: 'Later' })}
          </Button>
        </div>
      </div>
    </div>
  );
};

/** Compacte lijst "Te beoordelen" voor het medewerker-dashboard. */
export const TeBeoordelenLijst: React.FC<{ uitnodigingen: OpenReviewUitnodiging[] }> = ({ uitnodigingen }) => {
  const { t } = useTranslation('medewerker');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  if (uitnodigingen.length === 0) return null;
  const zichtbaar = open ? uitnodigingen : uitnodigingen.slice(0, 2);
  return (
    <section aria-labelledby="te-beoordelen-titel">
      <h2 id="te-beoordelen-titel" className="text-sm font-semibold text-gray-900 flex items-center gap-1.5 mb-2">
        <Star className="w-4 h-4 text-amber-500" aria-hidden="true" />
        {t('review.teBeoordelen', { defaultValue: 'Te beoordelen ({{n}})', n: uitnodigingen.length })}
      </h2>
      <ul className="space-y-2">
        {zichtbaar.map((u) => (
          <li key={u.id}>
            <button
              type="button"
              onClick={() => navigate(u.review_url)}
              className="w-full text-left bg-white rounded-xl border border-amber-200 p-3 flex items-center gap-3 hover:border-amber-400 transition-colors"
            >
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{u.professional_naam}</p>
                <p className="text-xs text-gray-500 truncate">
                  {u.bron_label}
                  {u.discipline_label ? ` · ${u.discipline_label}` : ''}
                </p>
              </div>
              <span className="text-sm text-[#0B1F3A] font-medium shrink-0">
                {t('review.beoordeel', { defaultValue: 'Beoordeel' })}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {uitnodigingen.length > 2 && (
        <button type="button" onClick={() => setOpen((v) => !v)} className="mt-2 text-sm text-[#0B1F3A] font-medium">
          {open ? t('review.minder', { defaultValue: 'Minder tonen' }) : t('review.alle', { defaultValue: 'Alle tonen' })}
        </button>
      )}
    </section>
  );
};
